import type { RoleOut } from "@sagot/api-client";
import { BUILT_IN_ROLES, consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Icon, cn } from "@sagot/ui";
import { useWorkspaceContext } from "../session/workspace-context";

/** Built-ins first (locked, lock icon + "Built-in"), then custom roles. */
export function RoleList({ roles, activeId }: { roles: readonly RoleOut[]; activeId: string | undefined }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const rank = (role: RoleOut) => {
    const index = BUILT_IN_ROLES.findIndex((key) => key === role.key);
    return role.is_builtin && index >= 0 ? index : BUILT_IN_ROLES.length;
  };
  const ordered = [...roles].sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
  return (
    <nav aria-label={t("roles.listLabel")}>
      <ul className="grid grid-cols-1 gap-px bg-line sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-1">
        {ordered.map((role) => {
          const active = role.id === activeId;
          return (
            <li key={role.id} className="bg-surface">
              <AppLink
                href={consoleRoutes.roles(summary.slug, role.id)}
                aria-current={active ? "page" : undefined}
                className={cn("flex items-center gap-3 px-5 py-3 hover:bg-surface-2", active && "bg-primary-soft hover:bg-primary-soft")}
              >
                <span className={cn("inline-flex size-8 shrink-0 items-center justify-center rounded-md", role.is_builtin ? "bg-sunken text-fg-muted" : "bg-primary-soft text-primary-ink")}>
                  <Icon name={role.is_locked ? "lock" : "shield-check"} className="size-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-semibold text-fg">{role.name}</span>
                    {role.is_builtin ? <Badge tone="neutral">{t("roles.builtIn")}</Badge> : <Badge tone="brand">{t("roles.custom")}</Badge>}
                  </span>
                  <span className="block text-xs text-fg-subtle">
                    {t("roles.members", { count: role.member_count })} · {t("roles.permissions", { count: role.permissions.length })}
                  </span>
                </span>
                <Icon name="chevron-right" className="size-4 text-fg-subtle" />
              </AppLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
